import type { ElementRef, RefObject } from "react";
import React, { useEffect, useMemo, useRef } from "react";

type ButtonType =
  | "plain"
  | "add-money"
  | "book"
  | "buy"
  | "check-out"
  | "continue"
  | "contribute"
  | "donate"
  | "order"
  | "pay"
  | "reload"
  | "rent"
  | "set-up"
  | "subscribe"
  | "support"
  | "tip"
  | "top-up";

export interface ApplePayButtonProps {
  buttonStyle?: "black" | "white" | "white-outline";
  type?: ButtonType;
  locale?: string;
  onClick?: (event: Event) => void;
  style?: {
    width?: string;
    height?: string;
    borderRadius?: string;
    padding?: string;
    boxSizing?: string;
  };
  disabled?: boolean;
}

const ApplePayButton: React.FC<ApplePayButtonProps> = ({
  buttonStyle = "black",
  type = "buy",
  locale = "en-US",
  onClick,
  style,
  disabled,
}) => {
  const canMakePayments = useMemo(() => {
    return !!ApplePaySession;
  }, []);
  console.log("canMakePayments", canMakePayments);
  const buttonRef = useRef<ElementRef<"div">>(null);

  const handleClick = (event: Event): void => {
    if (disabled !== true && onClick !== undefined) {
      onClick(event);
    }
  };

  useEffect(() => {
    if (buttonRef.current !== null) {
      buttonRef.current.addEventListener("click", handleClick);
    }

    return () => {
      if (buttonRef.current !== null) {
        buttonRef.current.removeEventListener("click", handleClick);
      }
    };
  }, [onClick, disabled]);

  const updateButtonStyle = (
    button?: Element | null,
    disabled?: boolean
  ): void => {
    const cursor = disabled === true ? "not-allowed" : "pointer";
    const opacity = disabled === true ? "0.5" : "1";
    button?.setAttribute("style", `cursor: ${cursor}; opacity: ${opacity};`);
  };

  const handleShadowRootChange = () => {
    if (
      buttonRef.current?.shadowRoot !== null &&
      buttonRef.current?.shadowRoot !== undefined
    ) {
      const button =
        buttonRef.current?.shadowRoot?.querySelector("div > button");
      updateButtonStyle(button, disabled);
      return true;
    }
    return false;
  };

  useEffect(() => {
    const wasHandled = handleShadowRootChange();
    let interval: NodeJS.Timeout | null = null;
    if (!wasHandled) {
      interval = setInterval(() => {
        const wasHandled = handleShadowRootChange();
        if (wasHandled && interval) {
          clearInterval(interval);
        }
      }, 100);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [disabled, buttonRef.current?.shadowRoot]);

  const createApplePayButtonStyle = (
    style?: ApplePayButtonProps["style"]
  ): string => `
    apple-pay-button {
        --apple-pay-button-width: ${style?.width ?? "auto"};
        --apple-pay-button-height: ${style?.height ?? "30px"};
        --apple-pay-button-border-radius: ${style?.borderRadius ?? "5px"};
        --apple-pay-button-padding: ${style?.padding ?? "5px 0px"};
        --apple-pay-button-box-sizing: ${style?.boxSizing ?? "border-box"};
        /* hide the button until the shadow root is available */
        display: ${
          buttonRef.current?.shadowRoot !== null ||
          buttonRef.current?.shadowRoot !== undefined
            ? "inline-block"
            : "none"
        };
    }
`;

  const createApplePayButton = (
    buttonStyle: string,
    type: string,
    locale: string,
    buttonRef: RefObject<ElementRef<"div">>
  ): React.ReactElement<
    {
      buttonstyle: string;
      type: string;
      locale: string;
      ref: RefObject<HTMLDivElement>;
    },
    string | React.JSXElementConstructor<"apple-pay-button">
  > | null => {
    if (!canMakePayments) {
      return null;
    }
    return React.createElement("apple-pay-button", {
      buttonstyle: buttonStyle,
      type,
      locale,
      ref: buttonRef,
    });
  };

  useEffect(() => {
    if (locale === "mao" && canMakePayments) {
      const applePayButton = document.querySelector("apple-pay-button");
      if (applePayButton && applePayButton.shadowRoot) {
        const shadowRoot = applePayButton.shadowRoot;
        const textElement = shadowRoot.querySelector("g text");
        if (textElement) {
          textElement.textContent = "Utu mā te ";
        }
      }
    }
  }, [locale, canMakePayments]);

  return (
    <>
      <style>{createApplePayButtonStyle(style)}</style>
      {createApplePayButton(buttonStyle, type, locale, buttonRef)}
    </>
  );
};

export default ApplePayButton;
